import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, FileDown, ArrowRight, X, Edit3, ShoppingCart, FileText,
  CheckCircle2, XCircle, Clock, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany } from "@/lib/company";
import { useAuth } from "@/lib/auth";

/** Compact currency formatter — mirrors the invoice pages. */
const fmtMoney = (v, cur = "INR") => {
  const sym = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ" }[cur] || (cur + " ");
  return `${sym}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function StatusChip({ status }) {
  const map = {
    draft: ["chip-neutral", Clock],
    sent: ["chip-info", FileText],
    accepted: ["chip-success", CheckCircle2],
    rejected: ["chip-danger", XCircle],
    expired: ["chip-warn", Clock],
    converted: ["chip-info", ArrowRight],
    confirmed: ["chip-success", CheckCircle2],
    invoiced: ["chip-info", ArrowRight],
    cancelled: ["chip-danger", XCircle],
  };
  const [cls, Icon] = map[status] || ["chip-neutral", Clock];
  return <span className={`chip ${cls} whitespace-nowrap`}><Icon size={11}/>{status}</span>;
}


/** Modal shared by both pages — draft / sent / etc. status via passed `kind`. */
function SalesDocModal({ open, kind, initial, onClose, onSaved }) {
  const { active } = useCompany();
  const cur = active?.base_currency || "INR";
  const isQuote = kind === "quotation";

  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    party_id: "", doc_date: new Date().toISOString().slice(0, 10),
    valid_until: "", expected_delivery: "",
    currency: cur, exchange_rate: 1, notes: "", reference: "",
    lines: [], status: "draft",
  });

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/parties?type=customer"), api.get("/items")])
      .then(([p, i]) => { setParties(p.data.parties || []); setItems(i.data.items || []); })
      .catch(() => {});
    if (initial) {
      setF({
        party_id: initial.party_id || "",
        doc_date: initial.doc_date || new Date().toISOString().slice(0, 10),
        valid_until: initial.valid_until || "",
        expected_delivery: initial.expected_delivery || "",
        currency: initial.currency || cur,
        exchange_rate: initial.exchange_rate || 1,
        notes: initial.notes || "", reference: initial.reference || "",
        lines: (initial.lines || []).map((l) => ({
          item_id: l.item_id || "", name: l.name, hsn: l.hsn || "",
          qty: l.qty, unit_price: l.unit_price,
          discount_pct: l.discount_pct || 0, tax_rate: l.tax_rate || 0,
        })),
        status: initial.status || "draft",
      });
    } else {
      setF((s) => ({ ...s,
        party_id: "", doc_date: new Date().toISOString().slice(0, 10),
        valid_until: isQuote ? new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10) : "",
        expected_delivery: isQuote ? "" : new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10),
        lines: [], status: "draft", notes: "",
      }));
    }
  }, [open, initial, cur, isQuote]);

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    f.lines.forEach((l) => {
      const qty = Number(l.qty || 0), price = Number(l.unit_price || 0);
      const disc = Number(l.discount_pct || 0), rate = Number(l.tax_rate || 0);
      const taxable = qty * price * (1 - disc / 100);
      sub += taxable; tax += taxable * (rate / 100);
    });
    return { sub, tax, grand: sub + tax };
  }, [f.lines]);

  const addLine = () => setF((s) => ({ ...s, lines: [...s.lines, { item_id: "", name: "", qty: 1, unit_price: 0, tax_rate: 18, discount_pct: 0, hsn: "" }] }));
  const updLine = (i, patch) => setF((s) => ({ ...s, lines: s.lines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  const rmLine = (i) => setF((s) => ({ ...s, lines: s.lines.filter((_, j) => j !== i) }));

  const onItemPick = (i, itemId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it) return updLine(i, { item_id: "" });
    updLine(i, {
      item_id: it.id, name: it.name, hsn: it.hsn || "",
      unit_price: it.sales_price || 0, tax_rate: it.tax_rate ?? 18,
    });
  };

  const submit = async () => {
    if (!f.party_id) return toast.error("Select a customer");
    if (f.lines.length === 0) return toast.error("Add at least one line");
    setSaving(true);
    try {
      const body = {
        kind,
        party_id: f.party_id, doc_date: f.doc_date,
        valid_until: isQuote ? (f.valid_until || null) : null,
        expected_delivery: isQuote ? null : (f.expected_delivery || null),
        currency: f.currency, exchange_rate: Number(f.exchange_rate || 1),
        notes: f.notes || null, reference: f.reference || null,
        status: f.status,
        lines: f.lines.map((l) => ({
          item_id: l.item_id || null, name: l.name, hsn: l.hsn || null,
          qty: Number(l.qty || 0), unit_price: Number(l.unit_price || 0),
          tax_rate: Number(l.tax_rate || 0), discount_pct: Number(l.discount_pct || 0),
        })),
      };
      const url = initial ? `/sales-docs/${initial.id}` : `/sales-docs`;
      const method = initial ? "put" : "post";
      const { data } = await api[method](url, body);
      toast.success(initial ? "Saved" : `${isQuote ? "Quotation" : "Sales Order"} created`);
      onSaved?.(data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="sales-doc-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl p-4 sm:p-6 my-4 sm:my-0 sm:max-h-[92vh] sm:overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">{isQuote ? "Quotation" : "Sales Order"}</div>
            <h2 className="text-lg sm:text-xl font-bold text-[#0F172A]">
              {initial ? `Edit ${initial.number}` : `New ${isQuote ? "Quotation" : "Sales Order"}`}
            </h2>
          </div>
          <button onClick={onClose} data-testid="sd-close" className="p-2" style={{ minWidth: 44, minHeight: 44 }}><X size={18}/></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="label-field">Customer *</label>
            <select data-testid="sd-party" className="input-clean" value={f.party_id} onChange={(e) => setF({ ...f, party_id: e.target.value })}>
              <option value="">Select customer</option>
              {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-field">{isQuote ? "Quotation Date" : "Order Date"} *</label>
            <input type="date" className="input-clean" value={f.doc_date} onChange={(e) => setF({ ...f, doc_date: e.target.value })}/>
          </div>
          {isQuote ? (
            <div>
              <label className="label-field">Valid Until</label>
              <input type="date" className="input-clean" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })}/>
            </div>
          ) : (
            <div>
              <label className="label-field">Expected Delivery</label>
              <input type="date" className="input-clean" value={f.expected_delivery} onChange={(e) => setF({ ...f, expected_delivery: e.target.value })}/>
            </div>
          )}
          <div>
            <label className="label-field">Reference</label>
            <input className="input-clean" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="PO # / RFQ #"/>
          </div>
        </div>

        <div className="mb-2 flex items-center justify-between">
          <label className="label-field">Lines</label>
          <button onClick={addLine} className="btn-secondary py-1 px-2 text-xs" data-testid="sd-add-line"><Plus size={12}/> Add Line</button>
        </div>
        {f.lines.length === 0 && <div className="text-sm text-[#94A3B8] italic mb-3">No lines added.</div>}
        {f.lines.map((l, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 mb-3 pb-3 border-b sm:border-none border-[#E2E8F0]" data-testid={`sd-line-${i}`}>
            <div className="col-span-2 sm:col-span-4">
              <label className="label-field sm:hidden">Item</label>
              <select className="input-clean" value={l.item_id} onChange={(e) => onItemPick(i, e.target.value)}>
                <option value="">-- Custom line --</option>
                {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
              </select>
              <input className="input-clean mt-1" placeholder="Description" value={l.name} onChange={(e) => updLine(i, { name: e.target.value })}/>
            </div>
            <div className="sm:col-span-1"><label className="label-field sm:hidden">Qty</label><input type="number" inputMode="decimal" className="input-clean" placeholder="Qty" value={l.qty} onChange={(e) => updLine(i, { qty: e.target.value })}/></div>
            <div className="sm:col-span-2"><label className="label-field sm:hidden">Rate</label><input type="number" inputMode="decimal" className="input-clean" placeholder="Rate" value={l.unit_price} onChange={(e) => updLine(i, { unit_price: e.target.value })}/></div>
            <div className="sm:col-span-2"><label className="label-field sm:hidden">Disc %</label><input type="number" inputMode="decimal" className="input-clean" placeholder="Disc %" value={l.discount_pct} onChange={(e) => updLine(i, { discount_pct: e.target.value })}/></div>
            <div className="sm:col-span-2"><label className="label-field sm:hidden">Tax %</label><input type="number" inputMode="decimal" className="input-clean" placeholder="Tax %" value={l.tax_rate} onChange={(e) => updLine(i, { tax_rate: e.target.value })}/></div>
            <div className="col-span-2 sm:col-span-1 flex items-center justify-end"><button onClick={() => rmLine(i)} className="text-[#DC2626] p-2" aria-label="Remove"><Trash2 size={14}/></button></div>
          </div>
        ))}

        <div className="mt-4 flex justify-end">
          <div className="w-full sm:w-72 space-y-1 text-sm font-mono-data">
            <div className="flex justify-between text-[#64748B]"><span>Subtotal</span><span>{fmtMoney(totals.sub, f.currency)}</span></div>
            <div className="flex justify-between text-[#64748B]"><span>Tax</span><span>{fmtMoney(totals.tax, f.currency)}</span></div>
            <div className="flex justify-between font-bold text-[#0F172A] pt-1 border-t border-[#E2E8F0]"><span>Grand Total</span><span data-testid="sd-grand">{fmtMoney(totals.grand, f.currency)}</span></div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="label-field">Status</label>
            <select className="input-clean" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
              {isQuote
                ? <><option value="draft">Draft</option><option value="sent">Sent</option><option value="accepted">Accepted</option></>
                : <><option value="draft">Draft</option><option value="confirmed">Confirmed</option></>}
            </select>
          </div>
          <div>
            <label className="label-field">Notes / Terms</label>
            <textarea rows={2} className="input-clean" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button data-testid="sd-save" onClick={submit} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">
            {saving ? "Saving…" : (initial ? "Save Changes" : `Create ${isQuote ? "Quotation" : "Sales Order"}`)}
          </button>
        </div>
      </div>
    </div>
  );
}


/** Generic page component — same shell for Quotations and Sales Orders. */
export function SalesCyclePage({ kind }) {
  const isQuote = kind === "quotation";
  const { active } = useCompany();
  const { role } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [converting, setConverting] = useState(null);

  const canWrite = ["owner", "admin", "sales", "super_admin"].includes(role);
  const cur = active?.base_currency || "INR";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sales-docs?kind=${kind}`);
      setRows(data.items || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (active) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [active, kind]);

  const del = async (row) => {
    if (!window.confirm(`Delete ${row.number}?`)) return;
    try { await api.delete(`/sales-docs/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const downloadPdf = async (row) => {
    try {
      const res = await api.get(`/sales-docs/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(row.number || "doc").replace(/\//g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF failed"); }
  };

  const convert = async (row) => {
    setConverting(row.id);
    try {
      const endpoint = isQuote ? "convert-to-so" : "convert-to-invoice";
      const { data } = await api.post(`/sales-docs/${row.id}/${endpoint}`);
      toast.success(isQuote
        ? `Sales Order ${data.sales_order.number} created`
        : `Invoice ${data.invoice.number} created`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion failed"); }
    finally { setConverting(null); }
  };

  return (
    <div className="space-y-4" data-testid={`${kind}-page`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Sales Cycle</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            {isQuote ? <FileText className="text-[#FF5500]" size={24}/> : <ShoppingCart className="text-[#FF5500]" size={24}/>}
            {isQuote ? "Quotations" : "Sales Orders"}
          </h1>
          <div className="text-sm text-[#64748B] mt-1">
            {isQuote
              ? "Draft proposals with expiry dates. Convert accepted quotes into sales orders in one click."
              : "Confirmed customer orders. Convert to a tax invoice when goods/services are delivered."}
          </div>
        </div>
        {canWrite && (
          <button className="btn-primary w-full sm:w-auto justify-center" onClick={() => { setEditing(null); setModalOpen(true); }} data-testid={`new-${kind}-btn`}>
            <Plus size={14}/> New {isQuote ? "Quotation" : "Sales Order"}
          </button>
        )}
      </div>

      <div className="card-surface p-3 sm:p-4">
        {loading ? (
          <div className="py-6 text-center text-[#64748B]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-[#64748B]" data-testid={`${kind}-empty`}>
            No {isQuote ? "quotations" : "sales orders"} yet.
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {rows.map((r) => (
                <div key={r.id} className="border border-[#E2E8F0] rounded-lg p-3 bg-white" data-testid={`sd-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-mono-data font-semibold text-[#0F172A] text-sm">{r.number}</div>
                      <div className="text-sm truncate">{r.party_snapshot?.name}</div>
                      <div className="text-[11px] text-[#64748B] font-mono-data mt-0.5">
                        {r.doc_date}{isQuote && r.valid_until ? ` · valid till ${r.valid_until}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-data font-bold text-[#0F172A]">{fmtMoney(r.grand_total, r.currency || cur)}</div>
                      <div className="mt-1"><StatusChip status={r.status}/></div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 items-center pt-2 border-t border-[#F1F5F9]">
                    {canWrite && !["converted", "invoiced", "cancelled"].includes(r.status) && (
                      <button onClick={() => { setEditing(r); setModalOpen(true); }} className="text-[#64748B] p-1" aria-label="Edit"><Edit3 size={16}/></button>
                    )}
                    <button onClick={() => downloadPdf(r)} className="text-[#0F172A] p-1" aria-label="PDF"><FileDown size={16}/></button>
                    {canWrite && r.status !== "converted" && r.status !== "invoiced" && r.status !== "cancelled" && (
                      <button
                        onClick={() => convert(r)} disabled={converting === r.id}
                        className="text-[#FF5500] p-1 flex items-center gap-1 text-xs font-semibold"
                        data-testid={`sd-convert-m-${r.id}`}
                      >
                        {converting === r.id ? <Loader2 size={14} className="animate-spin"/> : <ArrowRight size={14}/>}
                        {isQuote ? "→ Sales Order" : "→ Invoice"}
                      </button>
                    )}
                    {r.converted_to_so_id && (
                      <span className="text-xs text-[#64748B]">Linked SO</span>
                    )}
                    {r.converted_to_invoice_id && (
                      <Link to={`/app/invoices/${r.converted_to_invoice_id}`} className="text-[#2563EB] text-[11px] font-semibold">
                        View Invoice
                      </Link>
                    )}
                    {canWrite && r.status === "draft" && (
                      <button onClick={() => del(r)} className="text-[#DC2626] p-1 ml-auto" aria-label="Delete"><Trash2 size={16}/></button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <table className="table-clean hidden md:table">
              <thead>
                <tr>
                  <th>Number</th><th>Date</th><th>Customer</th>
                  <th>{isQuote ? "Valid Until" : "Expected"}</th>
                  <th className="text-right">Total</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`sd-row-${r.id}`}>
                    <td className="font-mono-data text-xs font-semibold text-[#0F172A]">{r.number}</td>
                    <td className="font-mono-data text-xs">{r.doc_date}</td>
                    <td>{r.party_snapshot?.name}</td>
                    <td className="font-mono-data text-xs">{isQuote ? r.valid_until : r.expected_delivery || "—"}</td>
                    <td className="text-right font-mono-data font-semibold">{fmtMoney(r.grand_total, r.currency || cur)}</td>
                    <td><StatusChip status={r.status}/></td>
                    <td>
                      <div className="flex gap-3 items-center justify-end">
                        {canWrite && !["converted", "invoiced", "cancelled"].includes(r.status) && (
                          <button onClick={() => { setEditing(r); setModalOpen(true); }} className="text-[#64748B] hover:text-[#FF5500]" aria-label="Edit"><Edit3 size={14}/></button>
                        )}
                        <button onClick={() => downloadPdf(r)} className="text-[#0F172A] hover:text-[#FF5500]" aria-label="PDF" data-testid={`sd-pdf-${r.id}`}><FileDown size={14}/></button>
                        {canWrite && r.status !== "converted" && r.status !== "invoiced" && r.status !== "cancelled" && (
                          <button
                            onClick={() => convert(r)} disabled={converting === r.id}
                            data-testid={`sd-convert-${r.id}`}
                            className="text-[#FF5500] hover:text-[#0F172A] flex items-center gap-1 text-xs font-semibold"
                          >
                            {converting === r.id ? <Loader2 size={12} className="animate-spin"/> : <ArrowRight size={12}/>}
                            {isQuote ? "→ SO" : "→ Invoice"}
                          </button>
                        )}
                        {r.converted_to_invoice_id && (
                          <Link to={`/app/invoices/${r.converted_to_invoice_id}`} className="text-[#2563EB] text-xs font-semibold">
                            Invoice
                          </Link>
                        )}
                        {canWrite && r.status === "draft" && (
                          <button onClick={() => del(r)} className="text-[#DC2626] hover:text-[#0F172A]" aria-label="Delete"><Trash2 size={14}/></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <SalesDocModal
        open={modalOpen} kind={kind} initial={editing}
        onClose={() => setModalOpen(false)} onSaved={load}
      />
    </div>
  );
}

export function Quotations() { return <SalesCyclePage kind="quotation"/>; }
export function SalesOrders() { return <SalesCyclePage kind="sales_order"/>; }
export default Quotations;
